import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { fetchRoleResume, updateRoleResumeOverrides, createRoleResume, fetchRoleResumeLatex, updateRoleResumeFromLatex } from '../api';
import EditableField from '../components/EditableField';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const RoleResumeViewer = () => {
  const { role } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [roleResume, setRoleResume] = useState(null);
  const [resumeData, setResumeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [expandedSection, setExpandedSection] = useState('experience');
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  // LaTeX editor state
  const [editorMode, setEditorMode] = useState('clean');
  const [latexContent, setLatexContent] = useState('');
  const [latexLoading, setLatexLoading] = useState(false);
  const [latexSaving, setLatexSaving] = useState(false);
  const [latexError, setLatexError] = useState(null);

  useEffect(() => {
    loadRoleResume();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  // Cleanup blob URLs
  useEffect(() => {
    return () => {
      if (pdfUrl && pdfUrl.startsWith('blob:')) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const loadRoleResume = async () => {
    try {
      setLoading(true);
      const data = await fetchRoleResume(decodeURIComponent(role), getToken());
      setRoleResume(data);
      setResumeData(data.content);
      setLoading(false);

      // Load PDF after resume data is loaded
      loadPDF();
    } catch (err) {
      console.error('Failed to load role resume:', err);
      alert('Failed to load role resume');
      navigate('/generate-resumes');
    }
  };

  const loadPDF = async () => {
    try {
      setPdfLoading(true);
      const timestamp = Date.now();
      const response = await fetch(`${API_URL}/resume/roles/${encodeURIComponent(decodeURIComponent(role))}/pdf?t=${timestamp}`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (!response.ok) {
        console.error('Failed to load PDF:', response.statusText);
        setPdfUrl(null);
        setPdfLoading(false);
        return;
      }

      const blob = await response.blob();

      // Revoke previous blob URL to prevent memory leaks
      if (pdfUrl && pdfUrl.startsWith('blob:')) {
        URL.revokeObjectURL(pdfUrl);
      }

      const blobUrl = URL.createObjectURL(blob);
      setPdfUrl(blobUrl);
      setPdfLoading(false);
    } catch (err) {
      console.error('Failed to load PDF:', err);
      setPdfUrl(null);
      setPdfLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(`${API_URL}/resume/roles/${encodeURIComponent(decodeURIComponent(role))}/pdf`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download PDF');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${displayName} Resume.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download PDF:', err);
      alert('Failed to download PDF');
    }
  };

  const handleRegenerate = async () => {
    if (!window.confirm('Regenerate this role resume? This will refresh it based on your current master resume.')) {
      return;
    }

    try {
      setRegenerating(true);
      await createRoleResume(decodeURIComponent(role), true, getToken());
      await loadRoleResume();
      alert('Role resume regenerated successfully!');
    } catch (err) {
      console.error('Failed to regenerate role resume:', err);
      alert('Failed to regenerate: ' + err.message);
    } finally {
      setRegenerating(false);
    }
  };

  const updateResumeField = async (path, value) => {
    try {
      setSaving(true);

      // Update local state
      const pathParts = path.split(/\.|\[|\]/).filter(Boolean);
      const newData = JSON.parse(JSON.stringify(resumeData));

      let current = newData;
      for (let i = 0; i < pathParts.length - 1; i++) {
        current = current[pathParts[i]];
      }
      current[pathParts[pathParts.length - 1]] = value;

      setResumeData(newData);

      // Save overrides to backend (send the resume data directly, not wrapped)
      await updateRoleResumeOverrides(decodeURIComponent(role), newData, getToken());

      // Reload PDF to reflect changes
      loadPDF();
    } catch (err) {
      console.error('Failed to update resume:', err);
      alert('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const addBulletPoint = async (path) => {
    const pathParts = path.split(/\.|\[|\]/).filter(Boolean);
    const newData = JSON.parse(JSON.stringify(resumeData));

    let current = newData;
    for (let i = 0; i < pathParts.length; i++) {
      current = current[pathParts[i]];
    }

    current.push('New bullet point');
    setResumeData(newData);

    try {
      await updateRoleResumeOverrides(decodeURIComponent(role), newData, getToken());
      loadPDF();
    } catch (err) {
      console.error('Failed to add bullet:', err);
    }
  };

  const deleteBulletPoint = async (path, index) => {
    if (!window.confirm('Delete this bullet point?')) return;

    const pathParts = path.split(/\.|\[|\]/).filter(Boolean);
    const newData = JSON.parse(JSON.stringify(resumeData));

    let current = newData;
    for (let i = 0; i < pathParts.length; i++) {
      current = current[pathParts[i]];
    }

    current.splice(index, 1);
    setResumeData(newData);

    try {
      await updateRoleResumeOverrides(decodeURIComponent(role), newData, getToken());
      loadPDF();
    } catch (err) {
      console.error('Failed to delete bullet:', err);
    }
  };

  // Load LaTeX content when switching to raw mode
  const loadLatexContent = useCallback(async () => {
    try {
      setLatexLoading(true);
      setLatexError(null);
      const data = await fetchRoleResumeLatex(decodeURIComponent(role), getToken());
      setLatexContent(data.latex_content);
    } catch (err) {
      console.error('Failed to load LaTeX:', err);
      setLatexError(err.message || 'Failed to load LaTeX content');
    } finally {
      setLatexLoading(false);
    }
  }, [role, getToken]);

  // Handle editor mode switch
  const handleEditorModeChange = async (mode) => {
    if (mode === 'raw' && editorMode !== 'raw') {
      // Switching to raw mode - load LaTeX
      await loadLatexContent();
    } else if (mode === 'clean' && editorMode !== 'clean') {
      // Switching back to clean mode - reload resume data (in case LaTeX was saved)
      await loadRoleResume();
    }
    setEditorMode(mode);
  };

  // Save LaTeX content
  const saveLatexContent = async () => {
    try {
      setLatexSaving(true);
      setLatexError(null);
      await updateRoleResumeFromLatex(decodeURIComponent(role), latexContent, getToken());
      loadPDF();
    } catch (err) {
      console.error('Failed to save LaTeX:', err);
      setLatexError(err.response?.data?.detail || err.message || 'Failed to save LaTeX. Check your syntax.');
    } finally {
      setLatexSaving(false);
    }
  };

  const displayName = roleResume?.display_name || decodeURIComponent(role).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-base)', color: 'var(--text-primary)', fontFamily: 'system-ui' }}>
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <div style={{ fontSize: '24px', marginBottom: '16px' }}>Loading role resume...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-base)', color: 'var(--text-primary)', fontFamily: 'system-ui' }}>
      {/* Header */}
      <div style={{
        padding: '16px 24px',
        borderBottom: '1px solid var(--border-light)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: 'var(--bg-surface)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => navigate('/generate-resumes')}
            style={{
              padding: '8px 16px',
              background: 'var(--border-light)',
              color: 'var(--text-primary)',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            ← Back
          </button>
          <div>
            <h1 style={{ fontSize: '20px', fontWeight: '700', margin: 0 }}>{displayName} Resume</h1>
            <p style={{ fontSize: '13px', color: 'var(--text-tertiary)', margin: 0 }}>
              Last updated: {roleResume?.last_generated_at ? new Date(roleResume.last_generated_at).toLocaleDateString() : 'Just now'}
            </p>
          </div>
          {roleResume?.is_stale && (
            <span style={{
              padding: '4px 12px',
              background: 'rgba(251, 191, 36, 0.2)',
              border: '1px solid rgba(251, 191, 36, 0.5)',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: '600',
              color: '#fbbf24'
            }}>
              NEEDS UPDATE
            </span>
          )}
          {roleResume?.has_overrides && (
            <span style={{
              padding: '4px 12px',
              background: 'rgba(34, 197, 94, 0.2)',
              border: '1px solid rgba(34, 197, 94, 0.5)',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: '600',
              color: '#4ade80'
            }}>
              CUSTOMIZED
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={loadPDF}
            style={{
              padding: '8px 16px',
              background: 'rgba(99, 102, 241, 0.2)',
              color: 'var(--text-primary)',
              border: '1px solid rgba(99, 102, 241, 0.5)',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Refresh PDF
          </button>
          <button
            onClick={handleDownload}
            style={{
              padding: '8px 16px',
              background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            Download PDF
          </button>
          <button
            onClick={handleRegenerate}
            disabled={regenerating}
            style={{
              padding: '8px 16px',
              background: regenerating ? 'rgba(255, 255, 255, 0.1)' : 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: regenerating ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              opacity: regenerating ? 0.6 : 1
            }}
          >
            {regenerating ? 'Regenerating...' : 'Regenerate'}
          </button>
        </div>
      </div>

      {/* Saving indicator */}
      {saving && (
        <div style={{
          position: 'fixed',
          top: '80px',
          right: '20px',
          padding: '12px 24px',
          background: 'rgba(99, 102, 241, 0.9)',
          color: 'white',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '600',
          zIndex: 1000
        }}>
          Saving...
        </div>
      )}

      {/* Main Content - Split View */}
      <div style={{ display: 'flex', height: 'calc(100vh - 73px)' }}>
        {/* Left Panel - Editor */}
        <div style={{
          width: '50%',
          overflowY: 'auto',
          padding: '24px',
          borderRight: '1px solid var(--border-light)'
        }}>
          {/* Editor Mode Toggle */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>Resume Content</h2>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => handleEditorModeChange('clean')}
                style={{
                  padding: '8px 16px',
                  background: editorMode === 'clean' ? 'rgba(99, 102, 241, 0.3)' : 'transparent',
                  border: '1px solid rgba(99, 102, 241, 0.5)',
                  borderRadius: '6px',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  fontWeight: editorMode === 'clean' ? '600' : '400',
                  fontSize: '13px'
                }}
              >
                Clean Editor
              </button>
              <button
                onClick={() => handleEditorModeChange('raw')}
                style={{
                  padding: '8px 16px',
                  background: editorMode === 'raw' ? 'rgba(245, 158, 11, 0.3)' : 'transparent',
                  border: '1px solid rgba(245, 158, 11, 0.5)',
                  borderRadius: '6px',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  fontWeight: editorMode === 'raw' ? '600' : '400',
                  fontSize: '13px'
                }}
              >
                Raw LaTeX
              </button>
            </div>
          </div>

          {editorMode === 'raw' ? (
            /* LaTeX Editor */
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3 style={{ margin: 0, fontSize: '16px' }}>Raw LaTeX Editor</h3>
                <button
                  onClick={saveLatexContent}
                  disabled={latexSaving}
                  style={{
                    padding: '8px 20px',
                    background: latexSaving ? 'rgba(99, 102, 241, 0.3)' : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    border: 'none',
                    borderRadius: '6px',
                    color: 'white',
                    cursor: latexSaving ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}
                >
                  {latexSaving ? 'Compiling...' : 'Compile & Save'}
                </button>
              </div>

              {latexError && (
                <div style={{
                  padding: '12px 16px',
                  background: 'rgba(239, 68, 68, 0.15)',
                  border: '1px solid rgba(239, 68, 68, 0.4)',
                  borderRadius: '8px',
                  color: '#f87171',
                  marginBottom: '12px',
                  fontSize: '13px'
                }}>
                  {latexError}
                </div>
              )}

              {latexLoading ? (
                <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-tertiary)' }}>
                  Loading LaTeX content...
                </div>
              ) : (
                <textarea
                  value={latexContent}
                  onChange={(e) => setLatexContent(e.target.value)}
                  style={{
                    width: '100%',
                    height: 'calc(100vh - 280px)',
                    padding: '16px',
                    background: 'var(--bg-elevated)',
                    border: '1px solid var(--border-light)',
                    borderRadius: '8px',
                    color: 'var(--text-primary)',
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    lineHeight: '1.5',
                    resize: 'none'
                  }}
                  spellCheck={false}
                />
              )}

              <p style={{ marginTop: '12px', fontSize: '12px', color: 'var(--text-tertiary)' }}>
                Edit the raw LaTeX code directly. Changes will be parsed back to structured data when saved.
              </p>
            </div>
          ) : (
            /* Clean Editor - Original content */
            <>
          {/* Experience Section */}
          <SectionCard
            title="Experience"
            icon="💼"
            expanded={expandedSection === 'experience'}
            onToggle={() => setExpandedSection(expandedSection === 'experience' ? null : 'experience')}
          >
            {resumeData?.experience?.map((exp, expIndex) => (
              <div key={expIndex} style={{
                padding: '16px',
                background: 'var(--bg-elevated)',
                border: '1px solid var(--border-light)',
                borderRadius: '12px',
                marginBottom: '12px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <div>
                    <EditableField
                      value={exp.job_title || ''}
                      onSave={(val) => updateResumeField(`experience[${expIndex}].job_title`, val)}
                      style={{ fontSize: '16px', fontWeight: '700' }}
                    />
                    <EditableField
                      value={exp.company_name || ''}
                      onSave={(val) => updateResumeField(`experience[${expIndex}].company_name`, val)}
                      style={{ color: 'var(--text-secondary)', fontSize: '14px' }}
                    />
                  </div>
                  <div style={{ textAlign: 'right', fontSize: '12px', color: 'var(--text-tertiary)' }}>
                    <div>{exp.location}</div>
                    <div>{exp.start_date} - {exp.end_date}</div>
                  </div>
                </div>

                <div style={{ marginTop: '12px' }}>
                  {exp.bullets?.map((bullet, bulletIndex) => (
                    <div key={bulletIndex} style={{ display: 'flex', alignItems: 'start', gap: '8px', marginBottom: '6px' }}>
                      <span style={{ color: 'var(--text-tertiary)', marginTop: '4px' }}>•</span>
                      <EditableField
                        value={bullet}
                        onSave={(val) => updateResumeField(`experience[${expIndex}].bullets[${bulletIndex}]`, val)}
                        multiline
                        style={{ flex: 1, fontSize: '13px', lineHeight: '1.5' }}
                      />
                      <button
                        onClick={() => deleteBulletPoint(`experience[${expIndex}].bullets`, bulletIndex)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'rgba(239, 68, 68, 0.7)',
                          cursor: 'pointer',
                          padding: '2px',
                          fontSize: '12px'
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => addBulletPoint(`experience[${expIndex}].bullets`)}
                    style={{
                      marginTop: '8px',
                      padding: '6px 12px',
                      background: 'rgba(99, 102, 241, 0.1)',
                      border: '1px dashed rgba(99, 102, 241, 0.3)',
                      borderRadius: '6px',
                      color: '#a78bfa',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    + Add Bullet
                  </button>
                </div>
              </div>
            ))}
          </SectionCard>

          {/* Projects Section */}
          <SectionCard
            title="Projects"
            icon="🚀"
            expanded={expandedSection === 'projects'}
            onToggle={() => setExpandedSection(expandedSection === 'projects' ? null : 'projects')}
          >
            {resumeData?.projects?.map((proj, projIndex) => (
              <div key={projIndex} style={{
                padding: '16px',
                background: 'var(--bg-elevated)',
                border: '1px solid var(--border-light)',
                borderRadius: '12px',
                marginBottom: '12px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <EditableField
                    value={proj.project_name || ''}
                    onSave={(val) => updateResumeField(`projects[${projIndex}].project_name`, val)}
                    style={{ fontSize: '16px', fontWeight: '700' }}
                  />
                  <span style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>{proj.date}</span>
                </div>

                <div style={{ marginTop: '12px' }}>
                  {proj.bullets?.map((bullet, bulletIndex) => (
                    <div key={bulletIndex} style={{ display: 'flex', alignItems: 'start', gap: '8px', marginBottom: '6px' }}>
                      <span style={{ color: 'var(--text-tertiary)', marginTop: '4px' }}>•</span>
                      <EditableField
                        value={bullet}
                        onSave={(val) => updateResumeField(`projects[${projIndex}].bullets[${bulletIndex}]`, val)}
                        multiline
                        style={{ flex: 1, fontSize: '13px', lineHeight: '1.5' }}
                      />
                      <button
                        onClick={() => deleteBulletPoint(`projects[${projIndex}].bullets`, bulletIndex)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'rgba(239, 68, 68, 0.7)',
                          cursor: 'pointer',
                          padding: '2px',
                          fontSize: '12px'
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => addBulletPoint(`projects[${projIndex}].bullets`)}
                    style={{
                      marginTop: '8px',
                      padding: '6px 12px',
                      background: 'rgba(99, 102, 241, 0.1)',
                      border: '1px dashed rgba(99, 102, 241, 0.3)',
                      borderRadius: '6px',
                      color: '#a78bfa',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    + Add Bullet
                  </button>
                </div>
              </div>
            ))}
          </SectionCard>

          {/* Skills Section */}
          <SectionCard
            title="Skills"
            icon="🛠️"
            expanded={expandedSection === 'skills'}
            onToggle={() => setExpandedSection(expandedSection === 'skills' ? null : 'skills')}
          >
            <div style={{
              padding: '16px',
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border-light)',
              borderRadius: '12px'
            }}>
              {Object.entries(resumeData?.skills || {}).map(([category, skills]) => (
                <div key={category} style={{ marginBottom: '12px' }}>
                  <h5 style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '4px', textTransform: 'capitalize' }}>
                    {category.replace(/_/g, ' ')}
                  </h5>
                  <p style={{ fontSize: '13px', color: 'var(--text-primary)', margin: 0 }}>
                    {Array.isArray(skills) ? skills.join(', ') : skills}
                  </p>
                </div>
              ))}
            </div>
          </SectionCard>
            </>
          )}
        </div>

        {/* Right Panel - PDF Preview */}
        <div style={{
          width: '50%',
          background: '#1a1a2e',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
            {pdfLoading && (
              <div style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                padding: '4px 10px',
                background: 'rgba(0,0,0,0.5)',
                borderRadius: '4px',
                fontSize: '12px',
                color: 'var(--text-tertiary)',
                zIndex: 1
              }}>Loading...</div>
            )}
            {pdfUrl ? (
              <iframe
                src={pdfUrl}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
                title="PDF"
              />
            ) : (
              <div style={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text-tertiary)',
                flexDirection: 'column',
                gap: '12px'
              }}>
                {pdfLoading ? (
                  <span>Generating PDF preview...</span>
                ) : (
                  <>
                    <span style={{ fontSize: '48px' }}>📄</span>
                    <span>PDF preview not available</span>
                    <button
                      onClick={loadPDF}
                      style={{
                        padding: '8px 16px',
                        background: 'rgba(99, 102, 241, 0.2)',
                        color: 'var(--text-primary)',
                        border: '1px solid rgba(99, 102, 241, 0.5)',
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      Generate PDF
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Collapsible Section Card Component
const SectionCard = ({ title, icon, expanded, onToggle, children }) => (
  <div style={{
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-light)',
    borderRadius: '12px',
    overflow: 'hidden',
    marginBottom: '16px'
  }}>
    <button
      onClick={onToggle}
      style={{
        width: '100%',
        padding: '14px 16px',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: 'var(--text-primary)'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span style={{ fontSize: '20px' }}>{icon}</span>
        <span style={{ fontSize: '16px', fontWeight: '700' }}>{title}</span>
      </div>
      <span style={{ fontSize: '16px', transform: expanded ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>
        ▼
      </span>
    </button>
    {expanded && (
      <div style={{ padding: '0 16px 16px' }}>
        {children}
      </div>
    )}
  </div>
);

export default RoleResumeViewer;
